import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { T, FONT, MONO, radius, tint, alpha } from './poyais-ui/theme';
import { Card, Grid, Eyebrow, Badge, Button } from './poyais-ui/primitives';
import { Icon } from './poyais-ui/icons';
import { exportCsv } from './poyais-ui/export';
import { useIsMobile } from './poyais-ui/hooks';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Genel Bakış': 'Overview', "Prompt'lar": 'Prompts', 'Rakipler': 'Competitors', 'Marka Algısı': 'Brand Perception',
  'Şimdi Tara': 'Scan Now', 'Taranıyor...': 'Scanning...', 'Prompt Ekle': 'Add Prompt',
  'Bu işlem 1-3 dakika sürebilir, sayfadan ayrılmayın.': 'This can take 1-3 minutes — please stay on this page.',
  'Tarama tamamlandı.': 'Scan complete.', 'sonuç eklendi.': 'result(s) added.',
  'istek motor hatası nedeniyle başarısız oldu (API kredi/limit kontrolü yapın).': 'request(s) failed due to an engine error (check API credit/limits).',
  'Görünürlük': 'Visibility', 'Ortalama Pozisyon': 'Avg. Position', 'Toplam Tarama': 'Total Scans', 'Son Tarama': 'Last Scan',
  'Henüz tarama yok': 'No scans yet',
  'Model Kırılımı': 'By Model', 'Share of Voice': 'Share of Voice', 'Görünürlük Trendi': 'Visibility Trend',
  'Son Sonuçlar': 'Recent Results', 'Markalar': 'Brands',
  'Başlamak için önce en az bir prompt ekleyin.': 'Add at least one prompt to get started.',
  "Prompt'lar sekmesine git": 'Go to Prompts tab',
  'motor için API anahtarı henüz tanımlı değil.': 'engine(s) have no API key configured yet.',
  'Takip Edilen Markalar': 'Tracked Brands', 'Kaydet': 'Save', 'Kaydedildi': 'Saved',
  'Marka Ekle': 'Add Brand', 'Marka adı': 'Brand name',
  'En fazla 10 marka takip edebilirsiniz.': 'You can track up to 10 brands.',
  'Bu markayı, tüm promptlarını ve tarama geçmişini silmek istediğinize emin misiniz?':
    'Delete this brand along with all its prompts and scan history?',
  'Önce en az bir marka ekleyin.': 'Add at least one brand first.',
  'Hangi marka?': 'Which brand?', 'Bu markayı tara': 'Scan this brand',
  'Bu marka için henüz tarama yok.': 'No scans for this brand yet.',
  'prompt': 'prompts', 'Henüz marka eklenmedi.': 'No brands added yet.',
  "Yeni Prompt": 'New Prompt', "Prompt metni (ör: \"En iyi CRM yazılımları hangileri?\")": 'Prompt text (e.g. "What are the best CRM tools?")',
  'Etiketler (virgülle ayır)': 'Tags (comma-separated)', 'Ekle': 'Add',
  'Aktif': 'Active', 'Pasif': 'Inactive', 'Henüz prompt eklenmedi.': 'No prompts added yet.',
  'Rakip marka adı': 'Competitor name', 'Henüz rakip eklenmedi.': 'No competitors added yet.',
  'Pozitif': 'Positive', 'Nötr': 'Neutral', 'Negatif': 'Negative',
  'AI Tarafından Atfedilen Özellikler': 'Attributes AI Associates', 'Yeterli veri yok.': 'Not enough data yet.',
  'Marka geçti': 'Mentioned', 'Marka geçmedi': 'Not mentioned', 'Pozisyon': 'Position',
  'Dışa Aktar': 'Export', 'Hata': 'Error', 'Veri alınamadı': 'Could not load data',
  'kayıt': 'entries', 'Tüm Markalar': 'All Brands',
  'AI ile Öner': 'Suggest with AI', 'Öneriler aranıyor...': 'Finding suggestions...',
  'Öneri bulunamadı, tekrar deneyin.': 'No suggestions found, try again.',
  'Eklemek için bir öneriye tıklayın:': 'Click a suggestion to add it:',
  'En Çok Atıf Alan Kaynaklar': 'Most-Cited Sources',
  'AI motorlarının bu markanın kategorisinde en sık referans aldığı siteler.':
    'The sites AI engines reference most often for this brand\'s category.',
  'atıf': 'citation',
});

const API = '/api/v1';

const MODEL_META: Record<string, { label: string; color: string }> = {
  chatgpt: { label: 'ChatGPT', color: T.green },
  perplexity: { label: 'Perplexity', color: T.blue },
  gemini: { label: 'Gemini', color: T.purple },
  claude: { label: 'Claude', color: T.amber },
};

const SENTIMENT_META: Record<string, { color: string; textColor: string }> = {
  positive: { color: T.greenTint, textColor: T.greenText },
  neutral: { color: T.trackBg, textColor: T.textDim },
  negative: { color: T.redTint, textColor: T.redText },
  not_mentioned: { color: T.trackBg, textColor: T.textFaint },
};

function Loader() { return <div className="py-pageload"><PoyaisSpinner size={38} pad={0}/></div>; }

function ErrorBox({ msg }: { msg: string }) {
  const { t } = useLang();
  return (
    <div style={{ background: tint(T.red, 8), border: `1px solid ${alpha(T.red, 25)}`, borderRadius: radius.lg, padding: '20px 24px', fontFamily: FONT }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: T.redText, marginBottom: 6 }}>{t('Hata')}</div>
      <div style={{ fontSize: 12, color: T.textMid }}>{msg}</div>
    </div>
  );
}

const inputStyle: React.CSSProperties = { fontFamily: FONT, fontSize: 12.5, padding: '9px 12px', borderRadius: radius.sm, border: `1px solid ${T.border}`, background: T.surface, color: T.text, outline: 'none' };

// Küçük yatay oran çubuğu (model kırılımı / SoV)
function MiniBar({ label, pct, color, strong }: { label: string; pct: number | null | undefined; color: string; strong?: boolean }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 4 }}>
        <span style={{ color: strong ? T.text : T.textMid, fontWeight: strong ? 800 : 700 }}>{label}</span>
        <span style={{ color: T.text, fontWeight: 700 }}>{pct !== undefined && pct !== null ? `%${pct}` : '—'}</span>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: T.trackBg }}>
        <div style={{ height: '100%', borderRadius: 999, width: `${pct || 0}%`, background: color }} />
      </div>
    </div>
  );
}

// ── GENEL BAKIŞ ────────────────────────────────────────────────────────────
function OverviewTab({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [data, setData] = useState<any>(null);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanningBrand, setScanningBrand] = useState<number | null>(null);
  const [scanNote, setScanNote] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [openBrands, setOpenBrands] = useState<Set<number>>(new Set());

  const load = () => {
    setLoading(true); setError(null);
    Promise.all([
      axios.get(`${API}/geo/overview`, { params: { client_id: clientId, days: 30 } }),
      axios.get(`${API}/geo/results`, { params: { client_id: clientId, days: 30 } }),
    ]).then(([o, r]) => { setData(o.data); setResults(r.data || []); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || t('Veri alınamadı')); setLoading(false); });
  };

  useEffect(load, [clientId]);

  const goToPrompts = () => window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'geo-prompts' }));

  const runScan = (brandId?: number) => {
    if (brandId) setScanningBrand(brandId); else setScanning(true);
    setScanNote(null); setScanError(null);
    axios.post(`${API}/geo/scan`, { client_id: clientId, ...(brandId ? { brand_id: brandId } : {}) })
      .then(r => {
        const skipped = r.data?.skipped_not_configured || 0;
        const scanned = r.data?.scanned || 0;
        const errored = r.data?.errored || 0;
        // Tarama başarıyla bittiğinde HER ZAMAN bir onay mesajı gösterilir — markanın
        // sonuçlarda hiç geçmediği (görünürlük %0) durumlarda ekranda görünür bir
        // değişiklik olmuyor, kullanıcı taramanın gerçekten çalışıp çalışmadığını
        // anlayamıyordu. "errored" (ör. kredi/limit hatası alan motor çağrıları) ayrıca
        // gösterilir — aksi halde "N sonuç eklendi" sayısı aslında boş/başarısız
        // kayıtları da içerip yanıltıcı oluyordu.
        let note = `${t('Tarama tamamlandı.')} ${scanned} ${t('sonuç eklendi.')}`;
        if (errored > 0) note += ` ${errored} ${t('istek motor hatası nedeniyle başarısız oldu (API kredi/limit kontrolü yapın).')}`;
        if (skipped > 0) note += ` ${skipped} ${t('motor için API anahtarı henüz tanımlı değil.')}`;
        setScanNote(note);
        setScanning(false); setScanningBrand(null);
        load();
      })
      .catch(e => { setScanError(e.response?.data?.detail || t('Veri alınamadı')); setScanning(false); setScanningBrand(null); });
  };

  const toggleBrand = (id: number) => setOpenBrands(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  if (loading) return <Loader />;
  if (error) return <ErrorBox msg={error} />;

  const brands: any[] = data?.brands || [];
  const noData = (data?.total_scans === 0) && results.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'stretch' : 'flex-end', gap: 6 }}>
        <Button variant="solid" color={T.accent} disabled={scanning} onClick={() => runScan()} style={{ width: isMobile ? '100%' : undefined }}>
          {scanning ? t('Taranıyor...') : t('Şimdi Tara')}
        </Button>
        {scanning && <span style={{ fontSize: 11, color: T.textDim }}>{t('Bu işlem 1-3 dakika sürebilir, sayfadan ayrılmayın.')}</span>}
      </div>
      {scanNote && (
        <div style={{ background: tint(T.amber, 10), border: `1px solid ${alpha(T.amber, 25)}`, borderRadius: radius.md, padding: '10px 16px', fontSize: 12, color: T.amberDeepText }}>{scanNote}</div>
      )}
      {scanError && (
        <div style={{ background: tint(T.red, 8), border: `1px solid ${alpha(T.red, 25)}`, borderRadius: radius.md, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ fontSize: 12, color: T.redText }}>{scanError}</div>
          {/* "Prompt Ekle" SADECE hata gerçekten "aktif prompt yok" ise gösterilir —
              aksi halde (ör. zaman aşımı) kullanıcıyı yanlışlıkla "prompt'un yok" sanıp
              yanlış yere yönlendiriyordu. */}
          {/aktif prompt yok/i.test(scanError) && (
            <Button variant="solid" color={T.accent} onClick={goToPrompts}>{t('Prompt Ekle')}</Button>
          )}
        </div>
      )}

      <Grid min={160} gap={14}>
        <Card pad={18}>
          <Eyebrow color={T.accent}>{t('Görünürlük')}</Eyebrow>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.text, fontFamily: MONO, marginTop: 8 }}>
            {data?.visibility_pct !== null && data?.visibility_pct !== undefined ? `%${data.visibility_pct}` : '—'}
          </div>
        </Card>
        <Card pad={18}>
          <Eyebrow color={T.blue}>{t('Ortalama Pozisyon')}</Eyebrow>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.text, fontFamily: MONO, marginTop: 8 }}>
            {data?.avg_position ?? '—'}
          </div>
        </Card>
        <Card pad={18}>
          <Eyebrow color={T.textDim}>{t('Toplam Tarama')}</Eyebrow>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.text, fontFamily: MONO, marginTop: 8 }}>{data?.total_scans ?? 0}</div>
        </Card>
        <Card pad={18}>
          <Eyebrow color={T.textDim}>{t('Son Tarama')}</Eyebrow>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginTop: 10 }}>
            {data?.last_scan_at ? data.last_scan_at.slice(0, 16).replace('T', ' ') : t('Henüz tarama yok')}
          </div>
        </Card>
      </Grid>

      {noData ? (
        <Card pad={30}>
          <div style={{ textAlign: 'center' as const, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            <Icon.search size={26} style={{ color: T.textDim }} />
            <div style={{ fontSize: 13, color: T.textMid }}>{t('Başlamak için önce en az bir prompt ekleyin.')}</div>
          </div>
        </Card>
      ) : (
        <>
          {data?.trend?.length > 1 && (
            <Card pad={20}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('Görünürlük Trendi')}</div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={data.trend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: T.textDim }} tickFormatter={v => (v || '').slice(5)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: T.textDim }} axisLine={false} tickLine={false} domain={[0, 100]} />
                  <Tooltip formatter={(v: any) => `%${v}`} />
                  <Line type="monotone" dataKey="visibility_pct" stroke={T.accent} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* ── Marka bazlı açılır/kapanır liste ── */}
          <Card pad={0}>
            <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 10 : 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{t('Markalar')}</div>
              <Button variant="ghost" color={T.accent} disabled={results.length === 0}
                onClick={() => exportCsv('ai-radar-sonuclar.csv', results.map(r => ({
                  brand: r.brand_name || '', prompt: r.prompt_text, model: r.model,
                  mentioned: r.brand_mentioned ? t('Marka geçti') : t('Marka geçmedi'),
                  position: r.mention_position ?? '', sentiment: r.sentiment, scanned_at: r.scanned_at,
                })), [
                  { key: 'brand', label: t('Markalar') }, { key: 'prompt', label: 'Prompt' }, { key: 'model', label: 'Model' },
                  { key: 'mentioned', label: t('Marka geçti') }, { key: 'position', label: t('Pozisyon') },
                  { key: 'sentiment', label: 'Sentiment' }, { key: 'scanned_at', label: t('Son Tarama') },
                ])}
                style={{ padding: '6px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon.download size={13} />{t('Dışa Aktar')}
              </Button>
            </div>

            {brands.length === 0 && (
              <div style={{ padding: '28px 14px', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Henüz marka eklenmedi.')}</div>
            )}

            {brands.map((b: any) => {
              const open = openBrands.has(b.brand_id);
              return (
                <div key={b.brand_id} style={{ borderBottom: `1px solid ${T.card}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '12px 16px' : '14px 24px' }}>
                    <button onClick={() => toggleBrand(b.brand_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, textAlign: 'left', padding: 0 }}>
                      <Icon.chevronRight size={14} style={{ color: T.textDim, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }} />
                      <span style={{ fontSize: 13.5, fontWeight: 800, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{b.brand_name}</span>
                      <span style={{ fontSize: 11, color: T.textDim, flexShrink: 0 }}>{b.prompt_count} {t('prompt')}</span>
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <Badge color={b.visibility_pct !== null && b.visibility_pct !== undefined ? T.accent : T.textFaint}>
                        {t('Görünürlük')} {b.visibility_pct !== null && b.visibility_pct !== undefined ? `%${b.visibility_pct}` : '—'}
                      </Badge>
                      {!isMobile && (
                        <Button variant="ghost" color={T.accent} disabled={scanningBrand === b.brand_id || b.prompt_count === 0}
                          onClick={() => runScan(b.brand_id)} style={{ padding: '4px 10px', fontSize: 10.5 }}>
                          {scanningBrand === b.brand_id ? t('Taranıyor...') : t('Bu markayı tara')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {open && (
                    <div style={{ padding: isMobile ? '0 16px 16px' : '0 24px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <Grid min={220} gap={12}>
                        <div style={{ border: `1px solid ${T.border}`, borderRadius: radius.md, padding: 14 }}>
                          <div style={{ fontSize: 11.5, fontWeight: 700, color: T.textMid, marginBottom: 12 }}>{t('Model Kırılımı')}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {Object.keys(MODEL_META).map(m => (
                              <MiniBar key={m} label={MODEL_META[m].label} pct={b.model_breakdown?.[m]} color={MODEL_META[m].color} />
                            ))}
                          </div>
                        </div>
                        <div style={{ border: `1px solid ${T.border}`, borderRadius: radius.md, padding: 14 }}>
                          <div style={{ fontSize: 11.5, fontWeight: 700, color: T.textMid, marginBottom: 12 }}>{t('Share of Voice')}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {(b.share_of_voice || []).slice(0, 5).map((s: any, i: number) => (
                              <MiniBar key={i} label={s.name} pct={s.pct} color={i === 0 ? T.accent : T.borderMid} strong={i === 0} />
                            ))}
                            {(!b.share_of_voice || b.share_of_voice.length === 0) && (
                              <div style={{ fontSize: 12, color: T.textDim }}>{t('Yeterli veri yok.')}</div>
                            )}
                          </div>
                        </div>
                        <div style={{ border: `1px solid ${T.border}`, borderRadius: radius.md, padding: 14 }}>
                          <div style={{ fontSize: 11.5, fontWeight: 700, color: T.textMid, marginBottom: 4 }}>{t('En Çok Atıf Alan Kaynaklar')}</div>
                          <div style={{ fontSize: 10.5, color: T.textFaint, marginBottom: 12, lineHeight: 1.4 }}>{t('AI motorlarının bu markanın kategorisinde en sık referans aldığı siteler.')}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {(b.top_sources || []).slice(0, 5).map((src: any, i: number) => {
                              const max = b.top_sources[0]?.count || 1;
                              return (
                                <a key={i} href={src.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 4 }}>
                                    <span style={{ color: i === 0 ? T.text : T.textMid, fontWeight: i === 0 ? 800 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{src.domain}</span>
                                    <span style={{ color: T.textDim, fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>×{src.count}</span>
                                  </div>
                                  <div style={{ height: 6, borderRadius: 999, background: T.trackBg }}>
                                    <div style={{ height: '100%', borderRadius: 999, width: `${Math.max(6, Math.round(src.count / max * 100))}%`, background: i === 0 ? T.accent : T.borderMid }} />
                                  </div>
                                </a>
                              );
                            })}
                            {(!b.top_sources || b.top_sources.length === 0) && (
                              <div style={{ fontSize: 12, color: T.textDim }}>{t('Yeterli veri yok.')}</div>
                            )}
                          </div>
                        </div>
                      </Grid>

                      {isMobile && (
                        <Button variant="ghost" color={T.accent} disabled={scanningBrand === b.brand_id || b.prompt_count === 0}
                          onClick={() => runScan(b.brand_id)} style={{ padding: '6px 12px', fontSize: 11, width: '100%' }}>
                          {scanningBrand === b.brand_id ? t('Taranıyor...') : t('Bu markayı tara')}
                        </Button>
                      )}

                      {(b.prompts || []).length === 0 ? (
                        <div style={{ fontSize: 12, color: T.textDim }}>{t('Bu marka için henüz tarama yok.')}</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {b.prompts.map((p: any) => (
                            <div key={p.prompt_id} style={{ border: `1px solid ${T.border}`, borderRadius: radius.md, padding: '10px 14px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 12, alignItems: isMobile ? 'flex-start' : 'center' }}>
                              <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{p.prompt_text}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const, flexShrink: 0 }}>
                                {(p.results || []).map((r: any, i: number) => (
                                  <span key={i} title={r.model} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700,
                                    padding: '3px 8px', borderRadius: 999,
                                    background: r.brand_mentioned ? tint(MODEL_META[r.model]?.color || T.green, 12) : T.trackBg,
                                    color: r.brand_mentioned ? (MODEL_META[r.model]?.color || T.greenText) : T.textFaint,
                                    border: `1px solid ${r.brand_mentioned ? alpha(MODEL_META[r.model]?.color || T.green, 25) : T.border}`,
                                  }}>
                                    {MODEL_META[r.model]?.label || r.model}
                                    {r.brand_mentioned ? (r.mention_position ? ` #${r.mention_position}` : ' ✓') : ' –'}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </>
      )}
    </div>
  );
}

// ── PROMPT'LAR ───────────────────────────────────────────────────────────────
function PromptsTab({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [brands, setBrands] = useState<any[]>([]);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newBrand, setNewBrand] = useState('');
  const [brandErr, setBrandErr] = useState<string | null>(null);
  const [editName, setEditName] = useState<Record<number, string>>({});
  const [newText, setNewText] = useState('');
  const [newTags, setNewTags] = useState('');
  const [newBrandId, setNewBrandId] = useState<number | ''>('');
  const [openBrands, setOpenBrands] = useState<Set<number>>(new Set());
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestErr, setSuggestErr] = useState<string | null>(null);

  const load = () => {
    Promise.all([
      axios.get(`${API}/geo/brands`, { params: { client_id: clientId } }),
      axios.get(`${API}/geo/prompts`, { params: { client_id: clientId } }),
    ]).then(([b, p]) => {
      const bl = b.data || [];
      setBrands(bl);
      setPrompts(p.data || []);
      setOpenBrands(new Set(bl.map((x: any) => x.id)));
      setNewBrandId(prev => (prev && bl.some((x: any) => x.id === prev)) ? prev : (bl[0]?.id ?? ''));
      setLoading(false);
    });
  };
  useEffect(load, [clientId]);

  const addBrand = () => {
    const name = newBrand.trim();
    if (!name) return;
    setBrandErr(null);
    axios.post(`${API}/geo/brands`, { client_id: clientId, name })
      .then(() => { setNewBrand(''); load(); })
      .catch(e => setBrandErr(e.response?.data?.detail || t('Veri alınamadı')));
  };
  const saveBrand = (id: number) => {
    const name = (editName[id] ?? '').trim();
    if (!name) return;
    axios.patch(`${API}/geo/brands/${id}`, { name }).then(() => {
      setEditName(prev => { const n = { ...prev }; delete n[id]; return n; });
      load();
    });
  };
  const removeBrand = (id: number) => {
    if (!window.confirm(t('Bu markayı, tüm promptlarını ve tarama geçmişini silmek istediğinize emin misiniz?'))) return;
    axios.delete(`${API}/geo/brands/${id}`).then(load);
  };

  const addPrompt = () => {
    if (!newText.trim() || !newBrandId) return;
    const tags = newTags.split(',').map(s => s.trim()).filter(Boolean);
    axios.post(`${API}/geo/prompts`, { client_id: clientId, brand_id: newBrandId, text: newText.trim(), tags }).then(() => {
      setNewText(''); setNewTags(''); load();
    });
  };
  const suggestPrompts = () => {
    if (!newBrandId) return;
    setSuggesting(true); setSuggestErr(null); setSuggestions([]);
    axios.post(`${API}/geo/prompts/suggest`, { client_id: clientId, brand_id: newBrandId })
      .then(r => {
        const s = r.data?.suggestions || [];
        if (s.length === 0) setSuggestErr(t('Öneri bulunamadı, tekrar deneyin.'));
        setSuggestions(s);
      })
      .catch(() => setSuggestErr(t('Öneri bulunamadı, tekrar deneyin.')))
      .finally(() => setSuggesting(false));
  };
  const addSuggestion = (text: string) => {
    axios.post(`${API}/geo/prompts`, { client_id: clientId, brand_id: newBrandId, text, tags: [] }).then(() => {
      setSuggestions(prev => prev.filter(s => s !== text));
      load();
    });
  };
  const toggleActive = (p: any) => axios.patch(`${API}/geo/prompts/${p.id}`, { active: !p.active }).then(load);
  const removePrompt = (id: number) => axios.delete(`${API}/geo/prompts/${id}`).then(load);
  const toggleOpen = (id: number) => setOpenBrands(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  if (loading) return <Loader />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      {/* Markalar */}
      <Card pad={20}>
        <Eyebrow color={T.accent}>{t('Takip Edilen Markalar')}</Eyebrow>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          {brands.map((b: any) => {
            const editing = editName[b.id] !== undefined;
            return (
              <div key={b.id} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 8, padding: '8px 0', borderBottom: `1px solid ${T.card}` }}>
                <input
                  value={editing ? editName[b.id] : b.name}
                  onChange={e => setEditName(prev => ({ ...prev, [b.id]: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && editing && saveBrand(b.id)}
                  style={{ ...inputStyle, flex: 1, maxWidth: isMobile ? undefined : 320 }} />
                <span style={{ fontSize: 11, color: T.textDim, minWidth: 64 }}>{b.prompt_count} {t('prompt')}</span>
                {editing && (
                  <Button variant="solid" color={T.accent} onClick={() => saveBrand(b.id)}>{t('Kaydet')}</Button>
                )}
                <button onClick={() => removeBrand(b.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textFaint, padding: 4 }}>
                  <Icon.trash size={15} />
                </button>
              </div>
            );
          })}
          {brands.length === 0 && <div style={{ fontSize: 12, color: T.textDim }}>{t('Henüz marka eklenmedi.')}</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, marginTop: 14 }}>
          <input value={newBrand} onChange={e => setNewBrand(e.target.value)} placeholder={t('Marka adı')}
            onKeyDown={e => e.key === 'Enter' && addBrand()}
            style={{ ...inputStyle, flex: 1, maxWidth: isMobile ? undefined : 320 }} disabled={brands.length >= 10} />
          <Button variant="solid" color={T.accent} onClick={addBrand} disabled={brands.length >= 10}
            style={{ color: T.accentInk, width: isMobile ? '100%' : undefined }}>{t('Marka Ekle')}</Button>
        </div>
        {brands.length >= 10 && <div style={{ fontSize: 11, color: T.textDim, marginTop: 6 }}>{t('En fazla 10 marka takip edebilirsiniz.')}</div>}
        {brandErr && <div style={{ fontSize: 11, color: T.redText, marginTop: 6 }}>{brandErr}</div>}
      </Card>

      {/* Yeni prompt */}
      <Card pad={20}>
        <Eyebrow color={T.textDim}>{t('Yeni Prompt')}</Eyebrow>
        {brands.length === 0 ? (
          <div style={{ fontSize: 12, color: T.textDim, marginTop: 10 }}>{t('Önce en az bir marka ekleyin.')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
              <select value={newBrandId} onChange={e => { setNewBrandId(Number(e.target.value)); setSuggestions([]); setSuggestErr(null); }}
                style={{ ...inputStyle, minWidth: isMobile ? undefined : 200 }}>
                {brands.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <input value={newText} onChange={e => setNewText(e.target.value)}
                placeholder={t('Prompt metni (ör: "En iyi CRM yazılımları hangileri?")')} style={{ ...inputStyle, flex: 1 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
              <input value={newTags} onChange={e => setNewTags(e.target.value)} placeholder={t('Etiketler (virgülle ayır)')} style={{ ...inputStyle, flex: 1 }} />
              <Button variant="ghost" color={T.accent} disabled={suggesting} onClick={suggestPrompts} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon.sparkle size={13} sw={2} />{suggesting ? t('Öneriler aranıyor...') : t('AI ile Öner')}
              </Button>
              <Button variant="solid" color={T.accent} onClick={addPrompt} style={{ width: isMobile ? '100%' : undefined }}>
                {t('Ekle')}
              </Button>
            </div>
            {suggestErr && <div style={{ fontSize: 11.5, color: T.textDim }}>{suggestErr}</div>}
            {suggestions.length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: T.textDim, marginBottom: 8 }}>{t('Eklemek için bir öneriye tıklayın:')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => addSuggestion(s)} style={{
                      display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: T.text,
                      background: 'var(--py-subtle-bg)', border: `1px solid ${T.border}`, borderRadius: radius.pill,
                      padding: '7px 13px', cursor: 'pointer', textAlign: 'left' as const,
                    }}>
                      <Icon.plus size={12} sw={2.3} style={{ color: T.accent, flexShrink: 0 }} />{s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Prompt'lar — marka bazlı gruplu */}
      {brands.map((b: any) => {
        const bp = prompts.filter((p: any) => p.brand_id === b.id);
        const open = openBrands.has(b.id);
        return (
          <Card key={b.id} pad={0}>
            <button onClick={() => toggleOpen(b.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', textAlign: 'left' }}>
              <Icon.chevronRight size={14} style={{ color: T.textDim, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
              <span style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>{b.name}</span>
              <span style={{ fontSize: 11, color: T.textDim }}>{bp.length} {t('prompt')}</span>
            </button>
            {open && (
              <div style={{ borderTop: `1px solid ${T.card}` }}>
                {bp.length === 0 ? (
                  <div style={{ padding: '20px 20px', fontSize: 12.5, color: T.textDim }}>{t('Henüz prompt eklenmedi.')}</div>
                ) : bp.map((p: any) => (
                  <div key={p.id} style={{
                    display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center',
                    gap: isMobile ? 10 : 12, padding: isMobile ? '14px 16px' : '14px 20px', borderBottom: `1px solid ${T.card}`,
                  }}>
                    <div style={{ flex: 1, width: isMobile ? '100%' : undefined }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{p.text}</div>
                      {p.tags?.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' as const }}>
                          {p.tags.map((tg: string, i: number) => <Badge key={i} color={T.textDim}>{tg}</Badge>)}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Button variant={p.active ? 'soft' : 'ghost'} color={p.active ? T.greenText : T.textDim} onClick={() => toggleActive(p)} style={{ padding: '4px 10px', fontSize: 10.5 }}>
                        {p.active ? t('Aktif') : t('Pasif')}
                      </Button>
                      <button onClick={() => removePrompt(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textFaint, padding: 4 }}>
                        <Icon.trash size={15} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ── RAKİPLER ─────────────────────────────────────────────────────────────────
function CompetitorsTab({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');

  const load = () => { axios.get(`${API}/geo/competitors`, { params: { client_id: clientId } }).then(r => { setItems(r.data || []); setLoading(false); }); };
  useEffect(load, [clientId]);

  const add = () => { if (!name.trim()) return; axios.post(`${API}/geo/competitors`, { client_id: clientId, name: name.trim() }).then(() => { setName(''); load(); }); };
  const remove = (id: number) => axios.delete(`${API}/geo/competitors/${id}`).then(load);

  if (loading) return <Loader />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      <Card pad={20}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder={t('Rakip marka adı')} style={{ ...inputStyle, flex: 1, maxWidth: isMobile ? undefined : 320 }} onKeyDown={e => e.key === 'Enter' && add()} />
          <Button variant="solid" color={T.accent} onClick={add} style={{ width: isMobile ? '100%' : undefined }}>
            {t('Ekle')}
          </Button>
        </div>
      </Card>
      <Card pad={0}>
        {items.length === 0 ? (
          <div style={{ padding: '28px 14px', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Henüz rakip eklenmedi.')}</div>
        ) : items.map((c: any) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: `1px solid ${T.card}` }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.name}</div>
            <button onClick={() => remove(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textFaint, padding: 4 }}>
              <Icon.trash size={15} />
            </button>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ── MARKA ALGISI ─────────────────────────────────────────────────────────────
function PerceptionTab({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState<any[]>([]);
  const [brandId, setBrandId] = useState<number | ''>('');

  useEffect(() => {
    axios.get(`${API}/geo/brands`, { params: { client_id: clientId } }).then(r => setBrands(r.data || []));
  }, [clientId]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/geo/perception`, { params: { client_id: clientId, days: 30, ...(brandId ? { brand_id: brandId } : {}) } })
      .then(r => { setData(r.data); setLoading(false); });
  }, [clientId, brandId]);

  const sc = data?.sentiment_counts || { positive: 0, neutral: 0, negative: 0 };
  const total = sc.positive + sc.neutral + sc.negative;
  const maxAttr = Math.max(1, ...(data?.top_attributes || []).map((a: any) => a.count));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, fontFamily: FONT }}>
      {brands.length > 1 && (
        <select value={brandId} onChange={e => setBrandId(e.target.value ? Number(e.target.value) : '')}
          style={{ ...inputStyle, maxWidth: isMobile ? undefined : 260 }}>
          <option value="">{t('Tüm Markalar')}</option>
          {brands.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}

      {loading ? <Loader /> : (
        <>
          <Grid min={150} gap={14}>
            {(['positive', 'neutral', 'negative'] as const).map(key => (
              <Card key={key} pad={18}>
                <Eyebrow color={SENTIMENT_META[key].textColor}>{key === 'positive' ? t('Pozitif') : key === 'neutral' ? t('Nötr') : t('Negatif')}</Eyebrow>
                <div style={{ fontSize: 24, fontWeight: 800, color: T.text, fontFamily: MONO, marginTop: 8 }}>{sc[key]}</div>
                <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>{total ? `%${Math.round(sc[key] / total * 100)}` : '—'}</div>
              </Card>
            ))}
          </Grid>

          <Card pad={20}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 14 }}>{t('AI Tarafından Atfedilen Özellikler')}</div>
            {(!data?.top_attributes || data.top_attributes.length === 0) ? (
              <div style={{ fontSize: 12, color: T.textDim }}>{t('Yeterli veri yok.')}</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 10 }}>
                {data.top_attributes.map((a: any, i: number) => {
                  const scale = 0.85 + (a.count / maxAttr) * 0.6;
                  return (
                    <div key={i} style={{
                      padding: '6px 14px', borderRadius: 999, background: tint(T.purple, 10),
                      border: `1px solid ${alpha(T.purple, 25)}`, fontSize: Math.round(11 * scale),
                      fontWeight: 700, color: T.purpleText,
                    }}>
                      {a.name} <span style={{ opacity: 0.6, fontWeight: 600 }}>· {a.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

// ── MAIN EXPORT ──────────────────────────────────────────────────────────────
export default function GeoVisibilityTab({ config, tab = 'overview' }: { config: any; tab?: string }) {
  const clientId = config?.client_id;
  if (!clientId) return <Loader />;
  if (tab === 'prompts') return <PromptsTab clientId={clientId} />;
  if (tab === 'competitors') return <CompetitorsTab clientId={clientId} />;
  if (tab === 'perception') return <PerceptionTab clientId={clientId} />;
  return <OverviewTab clientId={clientId} />;
}
